import { Button } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { useSignalConfigurationCRUDActions } from '@mm/state/actions/SignalConfigurationCRUDActions';
import {
  selectSignalConfiguration,
  selectSignalEventsByDay,
} from '@mm/state/selectors/signalSelectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { TABLET_SMALL } from '../../../../constants/screenSize';
import styles from './Signal.module.css';
import { prepareChartData } from './SignalGraph/helpers';
import {
  SignalGraph,
  signalRangeSelectOptions,
} from './SignalGraph/SignalGraph';
import { prepareStatsData } from './SignalStats/helpers';
import { SignalStats } from './SignalStats/SignalStats';

const emptyStatsData = {
  productView: 0,
  collectionView: 0,
  addToCart: 0,
  returnVisit: 0,
};

export const SignalHeaderText = () => (
  <div className={styles.headerText}>
    Signal — Enhanced website visitor action tracking
    <div className={styles.textSmall}>
      Send live event data on your website visitors to trigger different actions
    </div>
  </div>
);

export const Signal = () => {
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const history = useHistory();

  const chatbotId = useSelector(activeChatbotIdSelector);

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const { fetch: fetchConfiguration } = useSignalConfigurationCRUDActions();
  const { getEvents } = useSignalConfigurationCRUDActions();

  const configuration = useSelector(selectSignalConfiguration);
  const eventsByDay = useSelector(selectSignalEventsByDay);

  const [isInitialStateSet, setIsInitialStateSet] = useState(false);

  useEffect(() => {
    if (configuration) {
      setIsInitialStateSet(true);
    }
  }, [configuration]);

  const [statsData, setStatsData] = useState<{
    productView: number | null;
    collectionView: number | null;
    addToCart: number | null;
    returnVisit: number | null;
  }>(emptyStatsData);

  const [selectedRangeOption, setSelectedRangeOption] = useState(
    signalRangeSelectOptions[0].value,
  );

  const chartData = useMemo(() => {
    if (eventsByDay) {
      return prepareChartData(
        eventsByDay,
        selectedRangeOption,
        configuration?.mode || '',
      );
    }
    return null;
  }, [eventsByDay, selectedRangeOption, configuration?.mode]);

  useEffect(() => {
    if (activeFbPageId) {
      getEvents({
        facebookPageId: activeFbPageId,
        range: selectedRangeOption,
      });
      fetchConfiguration(activeFbPageId);
      if (isInitialStateSet) {
        setIsInitialStateSet(false);
      }
    }
  }, [activeFbPageId, selectedRangeOption]);

  useEffect(() => {
    if (eventsByDay && configuration) {
      const newStatsData = prepareStatsData(
        eventsByDay,
        selectedRangeOption,
        configuration.mode || '',
      );
      setStatsData(newStatsData);
    }
  }, [eventsByDay, selectedRangeOption, configuration]);

  const handleManageClick = () => {
    history.push(BotBuilderRoutes.signalSettings.getLink({ chatbotId }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        {!isMobile && <SignalHeaderText />}
        <Button
          css={{ container: styles.manageButton }}
          onClick={handleManageClick}
        >
          Manage your Signal Events
        </Button>
      </div>
      <SignalStats data={statsData} />
      <SignalGraph
        data={chartData}
        selectedRangeOption={selectedRangeOption}
        setSelectedRangeOption={setSelectedRangeOption}
      />
    </div>
  );
};
